import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";
import { getAuthHeader } from "../lib/auth";
import { openExternalUrl } from "../lib/appMode";
import crewHqBracketImage from "../data/crewHqBracketImage";

const TOURNAMENT_ID = "gas-up-3v3-crew-hq-2026";
const RETURN_PATH = "/tournaments/gas-up-3v3-crew-headquarters";

const FALLBACK = {
  id: TOURNAMENT_ID,
  name: "G.A.S UP Tournament",
  game: "NBA 2K27",
  mode: "3v3 Crew Headquarters",
  prizeCents: 30000,
  maxTeams: 16,
  conferenceSize: 8,
  seriesBestOf: 3,
  registrationOpen: false,
  status: "registration_prep",
  scheduledAt: null,
  teams: [],
  matches: [],
};

const FIRST_ROUND_Y = [34.8, 40.2, 48.7, 54.0, 62.3, 67.7, 73.5, 78.8];
const QUARTERFINAL_Y = [39.9, 51.8, 65.0, 76.8];
const SEMIFINAL_Y = [49.9, 64.5];

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(0)}`;
}

function signedIn() {
  return Boolean(getAuthHeader());
}

async function requestJSON(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.auth ? { Authorization: getAuthHeader() } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    mode: "cors",
    credentials: "omit",
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    err.data = data;
    throw err;
  }
  return data;
}

function teamLabel(team) {
  return String(team?.name || "TBD");
}

function matchById(matches, id) {
  return (Array.isArray(matches) ? matches : []).find((match) => match?.id === id) || null;
}

function BracketName({ team, score, top, left, right, align = "left", compact = false }) {
  if (!team) return null;
  return (
    <div
      style={{
        position: "absolute",
        top: `${top}%`,
        ...(left !== undefined ? { left } : {}),
        ...(right !== undefined ? { right } : {}),
        width: compact ? "9.6%" : "10.8%",
        transform: "translateY(-50%)",
        display: "flex",
        flexDirection: align === "right" ? "row-reverse" : "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 4,
        pointerEvents: "none",
        color: "#fff",
        fontWeight: 900,
        fontSize: compact ? "clamp(6px, .62vw, 11px)" : "clamp(7px, .72vw, 13px)",
        textShadow: "0 1px 4px #000, 0 0 7px #000",
      }}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{teamLabel(team)}</span>
      <span style={{ flexShrink: 0 }}>{Number(score || 0)}</span>
    </div>
  );
}

function BracketOverlay({ matches }) {
  const eastR1 = ["east-r1-1", "east-r1-2", "east-r1-3", "east-r1-4"];
  const westR1 = ["west-r1-1", "west-r1-2", "west-r1-3", "west-r1-4"];
  const eastR2 = ["east-r2-1", "east-r2-2"];
  const westR2 = ["west-r2-1", "west-r2-2"];
  const nodes = [];

  eastR1.forEach((id, index) => {
    const match = matchById(matches, id);
    if (!match) return;
    nodes.push(<BracketName key={`${id}-a`} team={match.teamA} score={match.scoreA} top={FIRST_ROUND_Y[index * 2]} left="2.8%" />);
    nodes.push(<BracketName key={`${id}-b`} team={match.teamB} score={match.scoreB} top={FIRST_ROUND_Y[index * 2 + 1]} left="2.8%" />);
  });
  westR1.forEach((id, index) => {
    const match = matchById(matches, id);
    if (!match) return;
    nodes.push(<BracketName key={`${id}-a`} team={match.teamA} score={match.scoreA} top={FIRST_ROUND_Y[index * 2]} right="2.8%" align="right" />);
    nodes.push(<BracketName key={`${id}-b`} team={match.teamB} score={match.scoreB} top={FIRST_ROUND_Y[index * 2 + 1]} right="2.8%" align="right" />);
  });
  eastR2.forEach((id, index) => {
    const match = matchById(matches, id);
    if (!match) return;
    nodes.push(<BracketName key={`${id}-a`} team={match.teamA} score={match.scoreA} top={QUARTERFINAL_Y[index * 2]} left="18.0%" compact />);
    nodes.push(<BracketName key={`${id}-b`} team={match.teamB} score={match.scoreB} top={QUARTERFINAL_Y[index * 2 + 1]} left="18.0%" compact />);
  });
  westR2.forEach((id, index) => {
    const match = matchById(matches, id);
    if (!match) return;
    nodes.push(<BracketName key={`${id}-a`} team={match.teamA} score={match.scoreA} top={QUARTERFINAL_Y[index * 2]} right="18.0%" align="right" compact />);
    nodes.push(<BracketName key={`${id}-b`} team={match.teamB} score={match.scoreB} top={QUARTERFINAL_Y[index * 2 + 1]} right="18.0%" align="right" compact />);
  });

  const eastFinal = matchById(matches, "east-final");
  const westFinal = matchById(matches, "west-final");
  if (eastFinal) {
    nodes.push(<BracketName key="east-final-a" team={eastFinal.teamA} score={eastFinal.scoreA} top={SEMIFINAL_Y[0]} left="31.5%" compact />);
    nodes.push(<BracketName key="east-final-b" team={eastFinal.teamB} score={eastFinal.scoreB} top={SEMIFINAL_Y[1]} left="31.5%" compact />);
  }
  if (westFinal) {
    nodes.push(<BracketName key="west-final-a" team={westFinal.teamA} score={westFinal.scoreA} top={SEMIFINAL_Y[0]} right="31.5%" align="right" compact />);
    nodes.push(<BracketName key="west-final-b" team={westFinal.teamB} score={westFinal.scoreB} top={SEMIFINAL_Y[1]} right="31.5%" align="right" compact />);
  }

  return <div style={styles.bracketOverlay}>{nodes}</div>;
}

export default function CrewHeadquartersTournament() {
  const [tournament, setTournament] = useState(FALLBACK);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [botBusy, setBotBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [form, setForm] = useState({ teamName: "", platform: "PlayStation 5", captainGamertag: "", teammateOne: "", teammateTwo: "" });
  const [inviteDetails, setInviteDetails] = useState({});
  const [scheduleDraft, setScheduleDraft] = useState("");
  const [lobbyOpen, setLobbyOpen] = useState(false);
  const [lobbyData, setLobbyData] = useState(null);
  const [lobbyLoading, setLobbyLoading] = useState(false);
  const [lobbyError, setLobbyError] = useState("");
  const [lobbyMessage, setLobbyMessage] = useState("");
  const [captainScoreDraft, setCaptainScoreDraft] = useState({ scoreA: 0, scoreB: 0 });
  const [captainFeedback, setCaptainFeedback] = useState("");
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeBusy, setDisputeBusy] = useState(false);
  const [disputeFiles, setDisputeFiles] = useState([]);
  const [disputeForm, setDisputeForm] = useState({ reasonType: "incorrect_score", explanation: "" });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const publicData = await requestJSON(`/tournaments/${TOURNAMENT_ID}`);
      setTournament(publicData?.tournament || FALLBACK);
      if (signedIn()) {
        const me = await requestJSON(`/tournaments/${TOURNAMENT_ID}/me`, { auth: true });
        setMine(me || null);
      } else {
        setMine(null);
      }
    } catch (err) {
      setError(err.message || "Could not load the Crew Headquarters tournament.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!tournament?.scheduledAt) {
      setScheduleDraft("");
      return;
    }
    const date = new Date(tournament.scheduledAt);
    if (Number.isNaN(date.getTime())) {
      setScheduleDraft("");
      return;
    }
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    setScheduleDraft(local.toISOString().slice(0, 16));
  }, [tournament?.scheduledAt]);

  const teams = useMemo(
    () => (Array.isArray(tournament?.teams) ? tournament.teams : []),
    [tournament?.teams]
  );
  const matches = useMemo(
    () => (Array.isArray(tournament?.matches) ? tournament.matches : []),
    [tournament?.matches]
  );
  const approvedTeams = useMemo(
    () => teams.filter((team) => ["approved", "eliminated", "champion"].includes(String(team?.status || "").toLowerCase())),
    [teams]
  );
  const spotsLeft = Math.max(0, Number(tournament?.maxTeams || 16) - approvedTeams.length);
  const myTeam = mine?.team || null;
  const myMatch = mine?.myMatch || mine?.captainMatch || null;
  const captainMatch = mine?.captainMatch || null;
  const captainMatchReport = mine?.captainMatchReport || null;
  const myDisputes = Array.isArray(mine?.myDisputes) ? mine.myDisputes : [];
  const currentUserId = String(mine?.user?.id || "");
  const currentUsername = String(mine?.user?.username || "").trim().toLowerCase();
  const isMyTeamCaptain = Boolean(
    myTeam &&
      (String(myTeam.captainUserId || "") === currentUserId ||
        (myTeam.members || []).some(
          (member) =>
            member?.role === "captain" &&
            String(member?.username || "").trim().toLowerCase() === currentUsername
        ))
  );
  const tournamentLive = ["in_progress", "active", "live"].includes(String(tournament?.status || "").toLowerCase());
  const myOpenDispute = myMatch?.id
    ? myDisputes.find(
        (entry) =>
          entry?.matchId === myMatch.id &&
          ["open", "under_review"].includes(String(entry?.status || "").toLowerCase())
      ) || null
    : null;
  const isChampionshipMatch = Boolean(
    myMatch &&
      (String(myMatch.id || "").toLowerCase() === "championship" ||
        String(myMatch.conference || "").toLowerCase() === "final")
  );
  const pendingInvites = Array.isArray(mine?.pendingInvites) ? mine.pendingInvites : [];
  const payoutReady = Boolean(mine?.payoutEligibility?.ready);
  const isAdmin = Boolean(mine?.isAdmin);
  const simulation = mine?.simulation || null;
  const completed = ["completed", "finished"].includes(String(tournament?.status || "").toLowerCase());
  const completion = tournament?.completionSnapshot || null;
  const dateText = tournament?.scheduledAt
    ? new Date(tournament.scheduledAt).toLocaleString()
    : "TBD — announced after all 16 bracket spots are filled";

  useEffect(() => {
    setCaptainScoreDraft({
      scoreA: Number(captainMatchReport?.scoreA ?? captainMatch?.scoreA ?? 0),
      scoreB: Number(captainMatchReport?.scoreB ?? captainMatch?.scoreB ?? 0),
    });
  }, [captainMatch?.id, captainMatch?.scoreA, captainMatch?.scoreB, captainMatchReport?.scoreA, captainMatchReport?.scoreB]);



  function updateForm(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function beginPayoutSetup() {
    setBusy(true);
    setError("");
    try {
      const data = await requestJSON("/connect/onboard", {
        method: "POST",
        auth: true,
        body: { returnPath: `${RETURN_PATH}?connect=1#register` },
      });
      if (!data?.url) throw new Error("Payout setup link was not returned.");
      await openExternalUrl(data.url);
    } catch (err) {
      setError(err.message || "Could not open payout setup.");
      setBusy(false);
    }
  }

  async function refreshPayout() {
    setBusy(true);
    setError("");
    try {
      await requestJSON("/connect/status", { auth: true });
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/payout-eligibility/refresh`, { method: "POST", auth: true });
      setMessage("Payout status refreshed.");
      await load();
    } catch (err) {
      setError(err.message || "Could not refresh payout status.");
    } finally {
      setBusy(false);
    }
  }

  async function createTeam(event) {
    event.preventDefault();
    if (!payoutReady) {
      setMessage("Complete Stripe payout setup first so all winners can receive the $100 prize automatically.");
      await beginPayoutSetup();
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/teams`, {
        method: "POST",
        auth: true,
        body: {
          teamName: form.teamName,
          platform: form.platform,
          captainGamertag: form.captainGamertag,
          teammateUsernames: [form.teammateOne, form.teammateTwo],
        },
      });
      setMessage(data?.delivery?.emailsSent === 2 ? "Team created. Both teammates received their tournament invites." : "Team created. Your teammates can accept their invites from this tournament page.");
      setForm({ teamName: "", platform: "PlayStation 5", captainGamertag: "", teammateOne: "", teammateTwo: "" });
      await load();
    } catch (err) {
      setError(err.message || "Could not create the tournament team.");
    } finally {
      setBusy(false);
    }
  }

  async function respondToInvite(teamId, decision) {
    const details = inviteDetails[teamId] || {};
    if (decision === "accept" && !payoutReady) {
      setMessage("Complete Stripe payout setup before accepting a prize-tournament roster spot.");
      await beginPayoutSetup();
      return;
    }
    setBusy(true);
    setError("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/invites/${teamId}/respond`, {
        method: "POST",
        auth: true,
        body: {
          decision,
          gamertag: decision === "accept" ? details.gamertag || "" : "",
          platform: decision === "accept" ? details.platform || "PlayStation 5" : "",
        },
      });
      setMessage(decision === "accept" ? "Roster spot confirmed." : "Tournament invite declined.");
      await load();
    } catch (err) {
      setError(err.message || "Could not answer the tournament invite.");
    } finally {
      setBusy(false);
    }
  }

  const loadMatchLobby = useCallback(async (matchId) => {
    if (!matchId) return;
    setLobbyLoading(true);
    setLobbyError("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${matchId}/lobby`, { auth: true });
      setLobbyData(data || null);
    } catch (err) {
      setLobbyError(err.message || "Could not load the headquarters match lobby.");
    } finally {
      setLobbyLoading(false);
    }
  }, []);

  async function openMatchLobby(matchId) {
    setLobbyOpen(true);
    setLobbyData(null);
    setLobbyMessage("");
    await loadMatchLobby(matchId);
  }

  async function sendLobbyMessage(event) {
    event.preventDefault();
    const matchId = lobbyData?.match?.id || myMatch?.id;
    const body = String(lobbyMessage || "").trim();
    if (!matchId || !body) return;
    setLobbyError("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${matchId}/lobby/messages`, {
        method: "POST",
        auth: true,
        body: { message: body },
      });
      setLobbyMessage("");
      await loadMatchLobby(matchId);
    } catch (err) {
      setLobbyError(err.message || "Could not send this lobby message.");
    }
  }

  function updateCaptainScore(side, value) {
    const clean = Math.max(0, Math.min(2, Number(value) || 0));
    setCaptainScoreDraft((current) => ({ ...current, [side]: clean }));
  }

  async function submitCaptainScore() {
    if (!captainMatch?.id) return;
    const scoreA = Number(captainScoreDraft.scoreA || 0);
    const scoreB = Number(captainScoreDraft.scoreB || 0);
    const valid =
      Number.isInteger(scoreA) && Number.isInteger(scoreB) &&
      scoreA >= 0 && scoreB >= 0 && scoreA <= 2 && scoreB <= 2 &&
      !(scoreA === 2 && scoreB === 2) && scoreA + scoreB > 0;
    if (!valid) {
      setCaptainFeedback("Enter a valid best-of-3 live score from 1–0 through 2–1.");
      return;
    }
    if (!window.confirm(`Report ${captainMatch.teamA?.name || "Team A"} ${scoreA}–${scoreB} ${captainMatch.teamB?.name || "Team B"}?`)) return;
    setBusy(true);
    setError("");
    setCaptainFeedback("");
    try {
      const result = await requestJSON(`/tournaments/${TOURNAMENT_ID}/matches/${captainMatch.id}/report`, {
        method: "POST", auth: true, body: { scoreA, scoreB },
      });
      const outcome = String(result?.outcome || "waiting").toLowerCase();
      if (outcome === "confirmed") setCaptainFeedback("Both captains matched on the final score. The winner advanced automatically.");
      else if (outcome === "live_confirmed") setCaptainFeedback("Both captains agree on the live score. Run the next game and update again.");
      else if (outcome === "disputed") setCaptainFeedback("The captain reports conflict. The series is locked for admin review.");
      else setCaptainFeedback("Your live score is saved. The opposing captain can submit their score now.");
      await load();
    } catch (err) {
      setCaptainFeedback(err.message || "Could not update the series score.");
    } finally {
      setBusy(false);
    }
  }

  async function submitMatchDispute(event) {
    event.preventDefault();
    if (!captainMatch?.id) return;
    const explanation = String(disputeForm.explanation || "").trim();
    if (explanation.length < 10) {
      setError("Give a short explanation of at least 10 characters.");
      return;
    }
    if (!window.confirm("Submit this match dispute and pause the series for admin review?")) return;
    setDisputeBusy(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("reasonType", disputeForm.reasonType);
      formData.append("explanation", explanation);
      disputeFiles.slice(0, 3).forEach((file) => formData.append("screenshots", file));
      const response = await fetch(apiUrl(`/tournaments/${TOURNAMENT_ID}/matches/${captainMatch.id}/disputes`), {
        method: "POST",
        headers: { Authorization: getAuthHeader() },
        body: formData,
        mode: "cors",
        credentials: "omit",
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.message || data?.error || `Request failed (${response.status})`);
      setMessage("Dispute submitted. The series is paused for tournament admin review.");
      setDisputeOpen(false);
      setDisputeFiles([]);
      setDisputeForm({ reasonType: "incorrect_score", explanation: "" });
      await load();
    } catch (err) {
      setError(err.message || "Could not submit this dispute.");
    } finally {
      setDisputeBusy(false);
    }
  }

  async function adminTeamDecision(teamId, decision) {
    setBusy(true);
    setError("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/teams/${teamId}/decision`, {
        method: "POST",
        auth: true,
        body: { decision },
      });
      setMessage(`Team ${decision} action saved.`);
      await load();
    } catch (err) {
      setError(err.message || "Could not update the team.");
    } finally {
      setBusy(false);
    }
  }

  async function setRegistration(open) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/registration`, {
        method: "POST",
        auth: true,
        body: { open },
      });
      setMessage(open ? "Crew HQ tournament registration is open." : "Crew HQ tournament registration is closed.");
      await load();
    } catch (err) {
      setError(err.message || "Could not change tournament registration.");
    } finally {
      setBusy(false);
    }
  }

  async function saveSchedule() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/schedule`, {
        method: "POST",
        auth: true,
        body: { scheduledAt: scheduleDraft ? new Date(scheduleDraft).toISOString() : null },
      });
      setMessage(scheduleDraft ? "Tournament date saved and ready to announce." : "Tournament date returned to TBD.");
      await load();
    } catch (err) {
      setError(err.message || "Could not save the tournament date.");
    } finally {
      setBusy(false);
    }
  }

  async function startRealTournament() {
    if (!window.confirm("Start the real Crew Headquarters tournament and lock the 16-team field?")) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await requestJSON(`/tournaments/${TOURNAMENT_ID}/admin/start`, { method: "POST", auth: true });
      setMessage("Crew Headquarters tournament started. The bracket is live.");
      await load();
    } catch (err) {
      setError(err.message || "Could not start the tournament.");
    } finally {
      setBusy(false);
    }
  }

  async function runBotAction(action, endpoint) {
    setBotBusy(action);
    setError("");
    setMessage("");
    try {
      const data = await requestJSON(`/tournaments/${TOURNAMENT_ID}${endpoint}`, { method: "POST", auth: true });
      setMessage(data?.message || `Bot simulator: ${action} complete.`);
      await load();
    } catch (err) {
      setError(err.message || `Could not ${action}.`);
    } finally {
      setBotBusy("");
    }
  }

  if (loading && !tournament) return <div style={styles.page}>Loading tournament…</div>;

  return (
    <div style={styles.page}>
      <div style={styles.topRow}>
        <Link to="/man-cave" style={styles.backLink}>← MAN CAVE</Link>
        <div style={styles.statusPill}>{String(tournament?.status || "UPCOMING").replaceAll("_", " ").toUpperCase()}</div>
      </div>

      <section style={styles.hero}>
        <div style={styles.kicker}>G.A.S UP • NBA 2K27 • CREW HEADQUARTERS</div>
        <h1 style={styles.title}>3v3 Tournament</h1>
        <div style={styles.heroStats}>
          <span>16 Teams</span><span>Best of 3</span><span>No OVR Cap</span><span>{money(tournament?.prizeCents || 30000)} Prize</span>
        </div>
        <div style={styles.dateLine}>📅 {dateText}</div>
        <div style={styles.heroActions}>
          <Link to="/tournaments/gas-up-3v3-crew-headquarters/live" style={styles.liveHubBtn}>📺 WATCH TOURNAMENT LIVE HUB</Link>
          {isMyTeamCaptain ? <Link to="/tournaments/gas-up-3v3-crew-headquarters/live#stream-this-match" style={styles.secondaryBtn}>🎥 STREAM THIS MATCH</Link> : null}
        </div>
      </section>

      {message ? <div style={styles.success}>{message}</div> : null}
      {error ? <div style={styles.error}>{error}</div> : null}

      <section style={styles.bracketCard}>
        <div style={styles.bracketWrap}>
          <img src={crewHqBracketImage} alt="G.A.S UP NBA 2K27 3v3 Crew Headquarters Tournament bracket" style={styles.bracketImage} />
          <BracketOverlay matches={matches} />
          {completed && completion ? (
            <div style={styles.championOverlay}>
              <div style={styles.championCrown}>🏆</div>
              <div style={styles.championLabel}>G.A.S UP CHAMPION</div>
              <div style={styles.championName}>{completion.teamName || "Champion"}</div>
              <div style={styles.championPrize}>$100 PER WINNING PLAYER</div>
            </div>
          ) : null}
        </div>
        <div style={styles.bracketFooter}>
          <strong>{approvedTeams.length}/16 bracket spots filled</strong>
          <span>{spotsLeft ? `${spotsLeft} spots remaining` : "BRACKET FULL — tournament date can now be announced"}</span>
        </div>
      </section>

      <section style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.eyebrow}>FORMAT</div>
          <h2 style={styles.cardTitle}>Crew Headquarters Rules</h2>
          <ul style={styles.rules}>
            <li>3 players per team. Captain creates the team and invites two GAStreaming account holders.</li>
            <li>Every round is a best-of-3 series.</li>
            <li><strong>No overall cap for this tournament.</strong></li>
            <li>For normal rounds, opposing captains coordinate which Crew Headquarters will host the series.</li>
            <li>If a participant is not in a crew or a usable headquarters is unavailable, the teams can use the G.A.S UP Crew Headquarters.</li>
            <li>The championship series is held in the G.A.S UP Crew Headquarters.</li>
            <li>Captains report results through GAStreaming. Disputed results are locked for admin review.</li>
          </ul>
        </div>

        <div style={styles.card} id="register">
          <div style={styles.eyebrow}>TEAM ENTRY</div>
          <h2 style={styles.cardTitle}>{myTeam ? "Your Tournament Team" : "Captain Registration"}</h2>

          {!signedIn() ? (
            <div style={styles.notice}>Sign in to create a team or accept a tournament invite.</div>
          ) : myTeam ? (
            <div style={styles.teamBox}>
              <div style={styles.teamName}>{myTeam.name}</div>
              <div>Status: <strong>{String(myTeam.status || "pending").replaceAll("_", " ").toUpperCase()}</strong></div>
              <div>{myTeam.conference ? `${myTeam.conference.toUpperCase()} #${myTeam.seed || "–"}` : "Bracket placement pending approval"}</div>
              <div style={styles.rosterList}>
                {(myTeam.members || []).map((member) => (
                  <div key={`${myTeam.id}-${member.userId || member.username}`} style={styles.rosterRow}>
                    <span>{member.username} {member.role === "captain" ? "(C)" : ""}</span>
                    <span>{String(member.status || "pending").toUpperCase()}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <form onSubmit={createTeam} style={styles.form}>
              <input required placeholder="Team name" value={form.teamName} onChange={(e) => updateForm("teamName", e.target.value)} style={styles.input} />
              <input required placeholder="Captain 2K gamertag" value={form.captainGamertag} onChange={(e) => updateForm("captainGamertag", e.target.value)} style={styles.input} />
              <select value={form.platform} onChange={(e) => updateForm("platform", e.target.value)} style={styles.input}>
                <option>PlayStation 5</option>
                <option>Xbox Series X|S</option>
              </select>
              <input required placeholder="Teammate 1 GA username" value={form.teammateOne} onChange={(e) => updateForm("teammateOne", e.target.value)} style={styles.input} />
              <input required placeholder="Teammate 2 GA username" value={form.teammateTwo} onChange={(e) => updateForm("teammateTwo", e.target.value)} style={styles.input} />
              <div style={payoutReady ? styles.payoutReady : styles.payoutWarn}>
                {payoutReady ? "✓ Stripe payout setup ready" : "Stripe payout setup is required for all prize participants."}
              </div>
              {!payoutReady ? (
                <div style={styles.buttonRow}>
                  <button type="button" disabled={busy} onClick={beginPayoutSetup} style={styles.secondaryBtn}>SET UP PAYOUTS</button>
                  <button type="button" disabled={busy} onClick={refreshPayout} style={styles.secondaryBtn}>REFRESH STATUS</button>
                </div>
              ) : null}
              <button disabled={busy || !tournament?.registrationOpen} style={styles.primaryBtn}>
                {tournament?.registrationOpen ? (busy ? "WORKING…" : "CREATE TEAM & SEND INVITES") : "REGISTRATION NOT OPEN YET"}
              </button>
            </form>
          )}
        </div>
      </section>

      {pendingInvites.length ? (
        <section style={styles.card}>
          <div style={styles.eyebrow}>YOUR INVITES</div>
          <h2 style={styles.cardTitle}>Tournament Team Invitations</h2>
          {pendingInvites.map((invite) => {
            const team = invite?.team || invite;
            const teamId = team?.id || invite?.teamId;
            const details = inviteDetails[teamId] || { gamertag: "", platform: "PlayStation 5" };
            return (
              <div key={teamId} style={styles.inviteBox}>
                <strong>{team?.name || "Tournament Team"}</strong>
                <input placeholder="Your 2K gamertag" value={details.gamertag || ""} onChange={(e) => setInviteDetails((current) => ({ ...current, [teamId]: { ...details, gamertag: e.target.value } }))} style={styles.input} />
                <select value={details.platform || "PlayStation 5"} onChange={(e) => setInviteDetails((current) => ({ ...current, [teamId]: { ...details, platform: e.target.value } }))} style={styles.input}>
                  <option>PlayStation 5</option><option>Xbox Series X|S</option>
                </select>
                <div style={styles.buttonRow}>
                  <button disabled={busy} onClick={() => respondToInvite(teamId, "accept")} style={styles.primaryBtn}>ACCEPT</button>
                  <button disabled={busy} onClick={() => respondToInvite(teamId, "decline")} style={styles.secondaryBtn}>DECLINE</button>
                </div>
              </div>
            );
          })}
        </section>
      ) : null}

      {myMatch?.id && myMatch?.teamA && myMatch?.teamB ? (
        <section id="match-report" style={styles.matchCard}>
          <div style={styles.eyebrow}>{isChampionshipMatch ? "🏆 CHAMPIONSHIP MATCHUP" : "YOUR ACTIVE MATCHUP"}</div>
          <h2 style={styles.cardTitle}>{myMatch.teamA.name} vs {myMatch.teamB.name}</h2>
          <div style={isChampionshipMatch ? styles.championshipVenue : styles.hqVenue}>
            {isChampionshipMatch
              ? "CHAMPIONSHIP VENUE LOCKED: G.A.S UP CREW HEADQUARTERS"
              : "HEADQUARTERS COORDINATION: Captains choose which crew HQ will host. If neither team has a usable HQ, use the G.A.S UP Crew Headquarters."}
          </div>
          <div style={styles.matchMetaRow}>
            <span>{myMatch.conference === "final" ? "CHAMPIONSHIP" : `${String(myMatch.conference || "").toUpperCase()} • ROUND ${myMatch.round || ""}`}</span>
            <span>BEST OF 3</span>
            <span>LIVE SCORE {Number(myMatch.scoreA || 0)}–{Number(myMatch.scoreB || 0)}</span>
          </div>
          <div style={styles.buttonRow}>
            <button type="button" onClick={() => openMatchLobby(myMatch.id)} style={styles.primaryBtn}>💬 OPEN HQ LOBBY & GAMERTAGS</button>
            <Link to="/tournaments/gas-up-3v3-crew-headquarters/live" style={styles.liveHubBtn}>📺 OPEN LIVE HUB</Link>
            {isMyTeamCaptain ? <Link to="/tournaments/gas-up-3v3-crew-headquarters/live#stream-this-match" style={styles.secondaryBtn}>🎥 STREAM THIS MATCH</Link> : null}
          </div>
          <div style={styles.smallText}>Use the private lobby to exchange 2K gamertags, decide which headquarters is hosting, send the Crew HQ invite, and tell the opponent when your team is ready.</div>

          {myOpenDispute ? (
            <div style={styles.reviewBanner}>⚠️ MATCH UNDER REVIEW — no team advances until an admin resolves the dispute.</div>
          ) : null}

          {isMyTeamCaptain && tournamentLive && captainMatch?.id ? (
            <div style={styles.captainPanel}>
              <div style={styles.eyebrow}>CAPTAIN LIVE SERIES REPORTING</div>
              <div style={styles.scoreGrid}>
                <label style={styles.scoreRow}>
                  <span>{captainMatch.teamA?.name}</span>
                  <input type="number" min="0" max="2" value={captainScoreDraft.scoreA} onChange={(event) => updateCaptainScore("scoreA", event.target.value)} style={styles.scoreInput} disabled={busy || Boolean(myOpenDispute)} />
                </label>
                <label style={styles.scoreRow}>
                  <span>{captainMatch.teamB?.name}</span>
                  <input type="number" min="0" max="2" value={captainScoreDraft.scoreB} onChange={(event) => updateCaptainScore("scoreB", event.target.value)} style={styles.scoreInput} disabled={busy || Boolean(myOpenDispute)} />
                </label>
              </div>
              <div style={styles.smallText}>Report after each game or wait until the series is over. Matching final 2–0 or 2–1 reports from both captains lock the result and advance the winner.</div>
              {captainMatchReport ? <div style={styles.reportStatus}>Current report status: {String(captainMatchReport.status || "waiting").replaceAll("_", " ").toUpperCase()}</div> : null}
              {captainFeedback ? <div style={styles.captainFeedback}>{captainFeedback}</div> : null}
              <div style={styles.buttonRow}>
                <button type="button" disabled={busy || Boolean(myOpenDispute)} onClick={submitCaptainScore} style={styles.botPrimaryBtn}>UPDATE LIVE SERIES SCORE</button>
                {!myOpenDispute ? <button type="button" disabled={busy} onClick={() => setDisputeOpen((value) => !value)} style={styles.smallDangerBtn}>DISPUTE MATCH</button> : null}
              </div>

              {disputeOpen && !myOpenDispute ? (
                <form onSubmit={submitMatchDispute} style={styles.disputeForm}>
                  <select value={disputeForm.reasonType} onChange={(event) => setDisputeForm((current) => ({ ...current, reasonType: event.target.value }))} style={styles.input} disabled={disputeBusy}>
                    <option value="incorrect_score">Incorrect score / result</option>
                    <option value="no_show">Opponent no-show</option>
                    <option value="unauthorized_roster">Unauthorized player / roster issue</option>
                    <option value="cheating_exploit">Cheating / exploit abuse</option>
                    <option value="other">Other issue</option>
                  </select>
                  <textarea rows="4" maxLength="1200" value={disputeForm.explanation} onChange={(event) => setDisputeForm((current) => ({ ...current, explanation: event.target.value }))} style={styles.textarea} placeholder="What happened? Include the game number and what the correct result should be." disabled={disputeBusy} />
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => setDisputeFiles(Array.from(event.target.files || []).slice(0, 3))} style={styles.fileInput} disabled={disputeBusy} />
                  {disputeFiles.length ? <div style={styles.smallText}>{disputeFiles.length} screenshot{disputeFiles.length === 1 ? "" : "s"} selected.</div> : null}
                  <button type="submit" style={styles.smallDangerBtn} disabled={disputeBusy}>{disputeBusy ? "SUBMITTING…" : "SUBMIT FOR ADMIN REVIEW"}</button>
                </form>
              ) : null}
            </div>
          ) : null}

          {lobbyOpen ? (
            <div style={styles.lobbyPanel}>
              <div style={styles.lobbyHeader}>
                <div>
                  <div style={styles.eyebrow}>PRIVATE HEADQUARTERS LOBBY</div>
                  <strong>{lobbyData?.match?.teamA?.name || myMatch.teamA.name} vs {lobbyData?.match?.teamB?.name || myMatch.teamB.name}</strong>
                </div>
                <button type="button" style={styles.smallBtn} onClick={() => setLobbyOpen(false)}>CLOSE</button>
              </div>
              {lobbyLoading && !lobbyData ? <div style={styles.smallText}>Loading lobby…</div> : null}
              {lobbyError ? <div style={styles.error}>{lobbyError}</div> : null}
              {lobbyData ? (
                <>
                  <div style={styles.lobbyRosters}>
                    {[lobbyData?.rosters?.teamA, lobbyData?.rosters?.teamB].filter(Boolean).map((team) => (
                      <div key={team.id} style={styles.lobbyRoster}>
                        <strong>{team.name}</strong>
                        {(team.members || []).map((member) => (
                          <div key={member.userId || member.username} style={styles.lobbyPlayer}>
                            <span>@{member.username}</span><span>{member.gamertag || "Gamertag missing"}</span><span>{member.platform || "Platform missing"}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div style={styles.lobbyMessages}>
                    {(lobbyData.messages || []).length ? (lobbyData.messages || []).map((entry) => (
                      <div key={entry.id} style={styles.lobbyMessage}>
                        <strong>@{entry.username}</strong>
                        <span>{entry.body}</span>
                      </div>
                    )) : <div style={styles.smallText}>No messages yet. Captains can decide the host HQ here.</div>}
                  </div>
                  <form onSubmit={sendLobbyMessage} style={styles.lobbyComposer}>
                    <input maxLength="500" value={lobbyMessage} onChange={(event) => setLobbyMessage(event.target.value)} placeholder="Example: We can host. Join our Crew HQ and we'll invite your squad." style={styles.input} />
                    <button type="submit" style={styles.primaryBtn}>SEND</button>
                  </form>
                  {!isChampionshipMatch ? (
                    <div style={styles.quickCopy}>Quick coordination ideas: “We can host at our HQ.” • “Send us the Crew HQ invite.” • “Let’s use the G.A.S UP HQ.”</div>
                  ) : (
                    <div style={styles.quickCopy}>Championship reminder: this series is played in the G.A.S UP Crew Headquarters.</div>
                  )}
                </>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : myTeam && tournamentLive ? (
        <section style={styles.matchCard}>
          <div style={styles.eyebrow}>MATCH STATUS</div>
          <h2 style={styles.cardTitle}>Waiting on your next matchup</h2>
          <div style={styles.smallText}>Once both sides of your bracket match are decided, your private Crew Headquarters lobby will appear here automatically.</div>
        </section>
      ) : null}

      {isAdmin ? (
        <section style={styles.adminCard}>
          <div style={styles.eyebrow}>FOUNDER ONLY</div>
          <h2 style={styles.cardTitle}>Crew HQ Tournament Control</h2>
          <div style={styles.adminSummary}>
            <span>Approved: {approvedTeams.length}/16</span>
            <span>Registration: {tournament?.registrationOpen ? "OPEN" : "CLOSED"}</span>
            <span>Date: {tournament?.scheduledAt ? new Date(tournament.scheduledAt).toLocaleDateString() : "TBD"}</span>
          </div>

          <div style={styles.adminControlPanel}>
            <div>
              <h3 style={styles.subTitle}>Event Controls</h3>
              <div style={styles.smallText}>The date stays TBD until all 16 approved bracket spots are filled. Test mode must be reset before changing live-event controls.</div>
            </div>
            <div style={styles.buttonRow}>
              <button disabled={busy || Boolean(simulation?.enabled)} onClick={() => setRegistration(true)} style={styles.smallBtn}>OPEN REGISTRATION</button>
              <button disabled={busy || Boolean(simulation?.enabled)} onClick={() => setRegistration(false)} style={styles.smallBtn}>CLOSE REGISTRATION</button>
            </div>
            <div style={styles.scheduleRow}>
              <input
                type="datetime-local"
                value={scheduleDraft}
                onChange={(event) => setScheduleDraft(event.target.value)}
                disabled={busy || spotsLeft > 0 || Boolean(simulation?.enabled)}
                style={styles.input}
              />
              <button
                disabled={busy || spotsLeft > 0 || Boolean(simulation?.enabled)}
                onClick={saveSchedule}
                style={styles.smallBtn}
              >
                SAVE TOURNAMENT DATE
              </button>
            </div>
            {spotsLeft > 0 ? <div style={styles.smallText}>Date control unlocks when the bracket reaches 16/16 approved teams.</div> : null}
            <button
              disabled={busy || spotsLeft > 0 || Boolean(simulation?.enabled) || ["in_progress", "active", "live", "completed", "finished"].includes(String(tournament?.status || "").toLowerCase())}
              onClick={startRealTournament}
              style={styles.botPrimaryBtn}
            >
              START REAL TOURNAMENT
            </button>
          </div>

          <div style={styles.adminSection}>
            <h3 style={styles.subTitle}>Team Review</h3>
            {(mine?.adminTeams || []).filter((team) => !team?.simulated).length ? (
              (mine?.adminTeams || []).filter((team) => !team?.simulated).map((team) => (
                <div key={team.id} style={styles.adminTeamRow}>
                  <div><strong>{team.name}</strong><div style={styles.smallText}>{String(team.status || "pending").replaceAll("_", " ")}</div></div>
                  <div style={styles.buttonRow}>
                    <button disabled={busy} onClick={() => adminTeamDecision(team.id, "approve")} style={styles.smallBtn}>APPROVE</button>
                    <button disabled={busy} onClick={() => adminTeamDecision(team.id, "waitlist")} style={styles.smallBtn}>WAITLIST</button>
                    <button disabled={busy} onClick={() => adminTeamDecision(team.id, "reject")} style={styles.smallDangerBtn}>REJECT</button>
                  </div>
                </div>
              ))
            ) : <div style={styles.smallText}>No real teams to review yet.</div>}
          </div>

          <div id="bot" style={styles.botPanel}>
            <div>
              <div style={styles.botTitle}>🤖 BOT / TEST TOURNAMENT SYSTEM</div>
              <div style={styles.smallText}>Same simulator as the original tournament. Bots are test-only, excluded from real participant email blasts, and never receive real Stripe payouts.</div>
            </div>
            <div style={styles.botStatus}>
              {simulation?.enabled ? `TEST MODE ACTIVE • ${simulation.botTeamCount || 0} bot teams` : "TEST MODE OFF"}
            </div>
            <div style={styles.botButtons}>
              {!simulation?.enabled ? (
                <button disabled={Boolean(botBusy)} onClick={() => runBotAction("fill bracket with bots", "/admin/simulator/fill")} style={styles.botBtn}>FILL OPEN SPOTS WITH BOTS</button>
              ) : (
                <>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("start test tournament", "/admin/start")} style={styles.botBtn}>START TEST TOURNAMENT</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("submit next bot result", "/admin/simulator/report-next")} style={styles.botBtn}>BOT REPORT NEXT</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("confirm next bot result", "/admin/simulator/confirm-next")} style={styles.botBtn}>CONFIRM NEXT</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("create test dispute", "/admin/simulator/dispute-next")} style={styles.botBtn}>TEST DISPUTE</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("resolve test dispute", "/admin/simulator/resolve-next")} style={styles.botBtn}>RESOLVE TEST DISPUTE</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("run full bot tournament", "/admin/simulator/run-full")} style={styles.botPrimaryBtn}>RUN FULL TOURNAMENT</button>
                  <button disabled={Boolean(botBusy)} onClick={() => runBotAction("reset test tournament", "/admin/simulator/reset")} style={styles.botDangerBtn}>RESET TEST MODE</button>
                </>
              )}
            </div>
            {simulation?.log?.length ? (
              <div style={styles.botLog}>
                {simulation.log.slice().reverse().slice(0, 6).map((entry) => (
                  <div key={entry.id || `${entry.action}-${entry.createdAt}`}>• {entry.message}</div>
                ))}
              </div>
            ) : null}
          </div>

          <div style={styles.adminLinks}>
            <Link to="/admin" style={styles.secondaryBtn}>OPEN FOUNDER COMMAND</Link>
            <Link to={`/admin/tournament-reviews?tournament=${encodeURIComponent(TOURNAMENT_ID)}`} style={styles.secondaryBtn}>DISPUTES</Link>
            <Link to={`/admin/tournament-payouts?tournament=${encodeURIComponent(TOURNAMENT_ID)}`} style={styles.secondaryBtn}>PAYOUTS</Link>
          </div>
        </section>
      ) : null}
    </div>
  );
}

const styles = {
  page: { width: "100%", maxWidth: 1320, margin: "0 auto", padding: "22px 14px 80px", boxSizing: "border-box", overflowX: "hidden", color: "#fff" },
  topRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 },
  backLink: { color: "#d7c6ff", fontWeight: 900, textDecoration: "none" },
  statusPill: { border: "1px solid rgba(185,80,255,.5)", borderRadius: 999, padding: "7px 12px", background: "rgba(104,28,170,.25)", fontSize: 12, fontWeight: 900 },
  hero: { padding: "24px 20px", borderRadius: 22, border: "1px solid rgba(110,43,255,.4)", background: "radial-gradient(circle at 20% 0%, rgba(130,36,255,.28), transparent 45%), radial-gradient(circle at 80% 20%, rgba(0,188,255,.2), transparent 45%), rgba(5,7,14,.92)", boxShadow: "0 24px 70px rgba(0,0,0,.45)" },
  kicker: { color: "#cda8ff", fontWeight: 900, fontSize: 12, letterSpacing: 2.2 },
  title: { margin: "8px 0 12px", fontSize: "clamp(34px,6vw,68px)", lineHeight: .95 },
  heroStats: { display: "flex", flexWrap: "wrap", gap: 8 },
  dateLine: { marginTop: 16, color: "rgba(255,255,255,.82)", fontWeight: 800 },
  heroActions: { marginTop: 14, display: "flex", flexWrap: "wrap", gap: 8 },
  liveHubBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", padding: "10px 13px", border: "1px solid rgba(0,210,255,.48)", borderRadius: 10, background: "linear-gradient(90deg,rgba(112,54,255,.92),rgba(0,143,202,.92))", color: "#fff", fontWeight: 900, cursor: "pointer", textDecoration: "none" },
  success: { marginTop: 14, padding: 12, borderRadius: 12, background: "rgba(0,190,110,.15)", border: "1px solid rgba(0,255,150,.3)" },
  error: { marginTop: 14, padding: 12, borderRadius: 12, background: "rgba(220,30,60,.18)", border: "1px solid rgba(255,80,100,.38)" },
  bracketCard: { marginTop: 18, borderRadius: 20, overflow: "hidden", background: "#07080f", border: "1px solid rgba(148,76,255,.4)", boxShadow: "0 25px 80px rgba(0,0,0,.55)" },
  bracketWrap: { position: "relative" },
  bracketImage: { width: "100%", height: "auto", display: "block" },
  bracketOverlay: { position: "absolute", inset: 0, pointerEvents: "none" },
  bracketFooter: { display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "12px 16px", background: "rgba(6,7,13,.96)" },
  championOverlay: { position: "absolute", left: "50%", top: "63%", transform: "translate(-50%,-50%)", minWidth: "26%", textAlign: "center", padding: "14px 16px", borderRadius: 16, background: "rgba(0,0,0,.88)", border: "2px solid #e7b943", boxShadow: "0 0 38px rgba(255,191,0,.35)" },
  championCrown: { fontSize: 30 },
  championLabel: { fontSize: 11, letterSpacing: 2, color: "#ffd76a", fontWeight: 900 },
  championName: { fontSize: "clamp(18px,2.1vw,34px)", fontWeight: 1000, marginTop: 5 },
  championPrize: { fontSize: 10, fontWeight: 900, color: "#ffd76a", marginTop: 4 },
  grid: { marginTop: 18, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))", gap: 16, minWidth: 0 },
  card: { marginTop: 18, padding: 18, borderRadius: 18, background: "rgba(8,9,17,.88)", border: "1px solid rgba(255,255,255,.12)" },
  adminCard: { marginTop: 18, padding: 18, borderRadius: 18, background: "linear-gradient(180deg,rgba(50,20,85,.92),rgba(8,9,17,.96))", border: "1px solid rgba(190,104,255,.45)" },
  eyebrow: { color: "#b991ff", fontSize: 11, letterSpacing: 2, fontWeight: 900 },
  cardTitle: { margin: "7px 0 12px", fontSize: 26 },
  rules: { margin: 0, paddingLeft: 20, lineHeight: 1.65, color: "rgba(255,255,255,.82)" },
  notice: { padding: 14, borderRadius: 12, background: "rgba(255,255,255,.06)", color: "rgba(255,255,255,.78)" },
  form: { display: "grid", gap: 10 },
  input: { width: "100%", boxSizing: "border-box", padding: "12px 13px", borderRadius: 10, border: "1px solid rgba(255,255,255,.18)", background: "rgba(0,0,0,.35)", color: "#fff", outline: "none" },
  payoutReady: { color: "#74ffbc", fontWeight: 900, fontSize: 13 },
  payoutWarn: { color: "#ffd36b", fontWeight: 800, fontSize: 13 },
  primaryBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", maxWidth: "100%", boxSizing: "border-box", padding: "11px 14px", border: "1px solid rgba(0,210,255,.6)", borderRadius: 10, background: "linear-gradient(90deg,#7638ff,#009ad8)", color: "#fff", fontWeight: 900, cursor: "pointer", textDecoration: "none", whiteSpace: "normal", textAlign: "center", overflowWrap: "anywhere" },
  secondaryBtn: { display: "inline-flex", alignItems: "center", justifyContent: "center", maxWidth: "100%", boxSizing: "border-box", padding: "10px 13px", border: "1px solid rgba(255,255,255,.18)", borderRadius: 10, background: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 800, cursor: "pointer", textDecoration: "none", whiteSpace: "normal", textAlign: "center", overflowWrap: "anywhere" },
  buttonRow: { display: "flex", flexWrap: "wrap", gap: 8, minWidth: 0, maxWidth: "100%" },
  teamBox: { padding: 14, borderRadius: 12, background: "rgba(122,56,255,.12)", border: "1px solid rgba(153,94,255,.25)", lineHeight: 1.6 },
  teamName: { fontSize: 23, fontWeight: 1000 },
  rosterList: { marginTop: 12, display: "grid", gap: 6 },
  rosterRow: { display: "flex", justifyContent: "space-between", gap: 10, borderTop: "1px solid rgba(255,255,255,.08)", paddingTop: 6 },
  inviteBox: { padding: 14, borderRadius: 12, border: "1px solid rgba(255,255,255,.12)", display: "grid", gap: 10, marginTop: 10 },
  matchCard: { marginTop: 18, padding: 18, borderRadius: 18, background: "linear-gradient(145deg,rgba(12,16,31,.94),rgba(37,10,64,.9))", border: "1px solid rgba(94,207,255,.28)", boxShadow: "0 18px 55px rgba(0,0,0,.34)" },
  hqVenue: { margin: "8px 0 12px", padding: "11px 13px", borderRadius: 12, background: "rgba(0,170,230,.1)", border: "1px solid rgba(0,210,255,.2)", color: "#bdefff", fontWeight: 850, lineHeight: 1.45 },
  championshipVenue: { margin: "8px 0 12px", padding: "11px 13px", borderRadius: 12, background: "rgba(216,166,38,.13)", border: "1px solid rgba(255,210,83,.36)", color: "#ffe28b", fontWeight: 1000, lineHeight: 1.45 },
  matchMetaRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, color: "rgba(255,255,255,.74)", fontSize: 12, fontWeight: 900 },
  reviewBanner: { marginTop: 12, padding: 11, borderRadius: 11, background: "rgba(190,30,50,.18)", border: "1px solid rgba(255,80,100,.32)", color: "#ffc3c8", fontWeight: 900 },
  captainPanel: { marginTop: 18, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,.1)", display: "grid", gap: 10 },
  scoreGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 9 },
  scoreRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: 11, borderRadius: 11, background: "rgba(0,0,0,.3)", fontWeight: 900 },
  scoreInput: { width: 62, padding: "8px 9px", borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "rgba(0,0,0,.5)", color: "#fff", textAlign: "center", fontWeight: 1000 },
  reportStatus: { color: "#d8c5ff", fontSize: 12, fontWeight: 900 },
  captainFeedback: { padding: 10, borderRadius: 10, background: "rgba(111,53,177,.16)", border: "1px solid rgba(180,117,255,.24)", color: "#eadbff", fontSize: 13, fontWeight: 800 },
  disputeForm: { marginTop: 6, padding: 12, borderRadius: 12, border: "1px solid rgba(255,90,110,.22)", background: "rgba(0,0,0,.25)", display: "grid", gap: 9 },
  textarea: { width: "100%", boxSizing: "border-box", padding: "11px 12px", borderRadius: 10, border: "1px solid rgba(255,255,255,.18)", background: "rgba(0,0,0,.35)", color: "#fff", resize: "vertical" },
  fileInput: { color: "rgba(255,255,255,.72)", fontSize: 12 },
  lobbyPanel: { marginTop: 16, padding: 14, borderRadius: 14, background: "rgba(2,5,12,.78)", border: "1px solid rgba(0,210,255,.22)" },
  lobbyHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 },
  lobbyRosters: { marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 10 },
  lobbyRoster: { padding: 12, borderRadius: 11, background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.09)", display: "grid", gap: 8 },
  lobbyPlayer: { display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 7, paddingTop: 6, borderTop: "1px solid rgba(255,255,255,.07)", fontSize: 12, color: "rgba(255,255,255,.75)", overflowWrap: "anywhere" },
  lobbyMessages: { marginTop: 12, maxHeight: 230, overflowY: "auto", padding: 10, borderRadius: 10, background: "rgba(0,0,0,.28)", display: "grid", gap: 8 },
  lobbyMessage: { display: "grid", gap: 3, fontSize: 13, color: "rgba(255,255,255,.78)" },
  lobbyComposer: { marginTop: 10, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(220px,100%),1fr))", gap: 8, minWidth: 0 },
  quickCopy: { marginTop: 9, color: "#a7ddff", fontSize: 11, lineHeight: 1.45 },
  adminSummary: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 },
  adminControlPanel: { marginTop: 16, padding: 14, borderRadius: 14, border: "1px solid rgba(255,255,255,.12)", background: "rgba(0,0,0,.2)", display: "grid", gap: 10 },
  scheduleRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(230px,100%),1fr))", gap: 8, alignItems: "center", minWidth: 0, maxWidth: "100%" },
  adminSection: { marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,.12)" },
  subTitle: { margin: "0 0 10px", fontSize: 18 },
  adminTeamRow: { display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,.08)", minWidth: 0 },
  smallText: { fontSize: 12, color: "rgba(255,255,255,.67)", lineHeight: 1.5 },
  smallBtn: { maxWidth: "100%", boxSizing: "border-box", padding: "7px 9px", borderRadius: 8, border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.08)", color: "#fff", fontWeight: 800, cursor: "pointer", whiteSpace: "normal", textAlign: "center", overflowWrap: "anywhere" },
  smallDangerBtn: { padding: "7px 9px", borderRadius: 8, border: "1px solid rgba(255,80,100,.35)", background: "rgba(180,20,40,.16)", color: "#fff", fontWeight: 800, cursor: "pointer" },
  botPanel: { marginTop: 18, padding: 16, borderRadius: 14, background: "rgba(0,0,0,.35)", border: "1px solid rgba(0,210,255,.26)" },
  botTitle: { fontSize: 18, fontWeight: 1000 },
  botStatus: { margin: "12px 0", display: "inline-flex", borderRadius: 999, padding: "7px 11px", background: "rgba(0,200,255,.1)", border: "1px solid rgba(0,200,255,.25)", fontSize: 12, fontWeight: 900 },
  botButtons: { display: "flex", flexWrap: "wrap", gap: 8 },
  botBtn: { maxWidth: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(0,210,255,.3)", background: "rgba(0,140,200,.12)", color: "#fff", fontWeight: 850, cursor: "pointer", whiteSpace: "normal", textAlign: "center", overflowWrap: "anywhere" },
  botPrimaryBtn: { maxWidth: "100%", boxSizing: "border-box", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(105,255,165,.45)", background: "rgba(0,170,90,.18)", color: "#fff", fontWeight: 900, cursor: "pointer", whiteSpace: "normal", textAlign: "center", overflowWrap: "anywhere" },
  botDangerBtn: { padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(255,70,100,.45)", background: "rgba(190,30,55,.18)", color: "#fff", fontWeight: 900, cursor: "pointer" },
  botLog: { marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,.08)", display: "grid", gap: 4, color: "rgba(255,255,255,.67)", fontSize: 12 },
  adminLinks: { marginTop: 16, display: "flex", flexWrap: "wrap", gap: 8 },
};
